import React, { ReactNode, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { ResourceParamFields } from './components/ResourceParamFields';
import FeedsList from './FeedsList';
import { TestSavePayload } from './TestSavePayload';
import { useDebugLogger } from 'new/hooks/useDebugLogger';

import classes from './SourceSetupPage.module.scss';
import DataSourceDetails from 'js/components/data_sources/add_ds_form/DataSourceDetails/DataSourceDetails';

interface SourceSetupPageProps {
  onNameChanged: (e) => void;
  name: string;
  selectedConnection: string;
  sourceConfig: any;
  formType: string;
  isEditMode: boolean;
  description?: string;
  onDescriptionChanged?: (e) => void;
  isLoadingInitialData?: boolean;
  // Template version information passed from parent
  templateVersionInfo?: {
    templateVersion: 'v1' | 'v2';
    isV2Template: boolean;
  };
  // Flow type for conditional display logic
  flowType?: string;
}

export const ELTSourceSetup = (props: SourceSetupPageProps) => {
  const {
    onNameChanged,
    name,
    description,
    isLoadingInitialData,
    onDescriptionChanged,
    selectedConnection,
    formType,
    isEditMode,
    templateVersionInfo,
    flowType = 'elt', // Default to 'elt' for backward compatibility
  } = props;
  const topRef = useRef();
  const debugLogger = useDebugLogger();

  // Memoize the debug component to prevent unnecessary re-renders
  const debugComponent = useMemo(() => {
    return debugLogger.isDebugEnabled ? <TestSavePayload formType={formType} /> : null;
  }, [debugLogger.isDebugEnabled, formType]);

  // get sourceConfig from state.forms[formType].sourceConfig but only when it changes
  const sourceConfig = useSelector((state: any) => {
    return state.forms[formType]?.data?.sourceConfig;
  });

  // get vendorFeedSpecs from state.forms[formType].vendorFeedSpecs
  const vendorFeedSpecs = useSelector((state: any) => {
    return state.forms[formType]?.data?.vendorFeedSpecs;
  });

  const metas = useSelector((state: any) => state.paging.metaInfoById);
  const meta = metas[selectedConnection];

  const handleInputChange = ({ property, value }) => {
    if (property === 'sourceName') {
      onNameChanged(value);
    } else if (property === 'sourceDescription') {
      onDescriptionChanged(value);
    }
  };

  if (isLoadingInitialData) {
    return <div className={classes.root}></div>;
  }

  return (
    <div className={classes.root}>
      <DataSourceDetails
        name={name || ''}
        description={description || ''}
        onInputChange={handleInputChange}
        selectedConnectionDetails={meta}
      />
      <div className={classes.fieldsContainer}>
        <div className={classes.inputs}>
          <div className={classes.inputsGrid}>
            <ResourceParamFields
              formType={formType}
              selectedConnection={selectedConnection}
              sourceConfig={sourceConfig}
              vendorFeedSpecs={vendorFeedSpecs}
              templateVersionInfo={templateVersionInfo}
              flowType={flowType}
            />
          </div>
        </div>
        {debugComponent}
        <FeedsList
          formType={formType}
          isEditMode={isEditMode}
          templateVersionInfo={templateVersionInfo}
        />
      </div>
    </div>
  );
};
